import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { magnetic } from '@schmancy/directives/magnetic'
import { type ControlSize, sizeClasses } from '@schmancy/theme/control-size'
import { type SchmancyTone, type ToneVariant, toneColors } from '@schmancy/theme/tone'
import '@schmancy/primitives/icons/icon'
import '@schmancy/primitives/busy/spinner'
import { takeUntil, tap } from 'rxjs/operators'
import { chipTextChanges, removeName } from './remove-name'

/**
 * Filter chip component for content filtering.
 *
 * Filter chips are the ONLY chip type that maintains persistent selected state.
 * They are used for filtering content by toggling on/off different filter criteria.
 *
 * ## The selected look
 * `color` picks the hue the selected pill carries from the tone ladder and
 * `variant` picks how hard it presses that hue — the same two axes
 * `<schmancy-assist-chip>` and every other tonal mark in the family carry.
 * Unset `color` is the untoned default: `secondary`'s tinted pair, which is
 * exactly `toneColors('secondary', 'soft')`.
 *
 * The tone writes three CSS custom properties, and those are the escape hatch
 * for a selected look the ladder does not name:
 *
 * | Property                      | Untoned default (resolved at runtime)           |
 * |-------------------------------|-------------------------------------------------|
 * | --filter-chip-selected-bg     | var(--schmancy-sys-color-secondary-container)   |
 * | --filter-chip-selected-color  | var(--schmancy-sys-color-secondary-onContainer) |
 * | --filter-chip-selected-border | transparent                                     |
 *
 * ```css
 * schmancy-filter-chip.my-chip {
 *   --filter-chip-selected-bg:     oklch(from var(--schmancy-sys-color-primary-default) l c h / 0.12);
 *   --filter-chip-selected-color:  var(--schmancy-sys-color-primary-default);
 *   --filter-chip-selected-border: oklch(from var(--schmancy-sys-color-primary-default) l c h / 0.40);
 * }
 * ```
 *
 * A set `color` writes those three properties onto the control itself, so it
 * wins over a host-level override of the same three.
 *
 * ## Slots
 * - (default) — chip label / content
 * - `leading`  — rendered inside the button BEFORE the default slot; use for
 *               an avatar circle, icon, or any leading decoration. Laid out
 *               with `contents` display so it participates in the button's
 *               flex row without adding an extra layer.
 * - `trailing` — rendered inside the button AFTER the default slot and BEFORE
 *               the removable close button; use for a drill-in icon button or
 *               any trailing affordance. Laid out with `contents` display so
 *               it participates in the button's flex row without adding an
 *               extra layer.
 *
 * ## Size and compact axes
 * `size` selects geometry from the shared control-size ladder; `compact` is a
 * separate 24px/11px dense-pill preset that always wins over `size` when set —
 * see the `size`/`compact` property docs below.
 *
 * @fires change - Dispatched when selection state changes with {value, selected}
 * @fires remove - Dispatched when remove button is clicked (if removable)
 *
 * @example — standard (unchanged behaviour)
 * ```html
 * <schmancy-filter-chip value="category-1" selected>
 *   Category 1
 * </schmancy-filter-chip>
 * ```
 *
 * @example — an achromatic scope switch: the selected pill inverts rather than tints
 * ```html
 * <schmancy-chips required .value=${scope}>
 *   <schmancy-filter-chip size="sm" color="surface" variant="solid" value="all">All 6</schmancy-filter-chip>
 *   <schmancy-filter-chip size="sm" color="surface" variant="solid" value="held">4 still with you</schmancy-filter-chip>
 * </schmancy-chips>
 * ```
 *
 * @example — compact pill with tinted avatar leading slot
 * ```html
 * <schmancy-filter-chip
 *   compact
 *   value="agent-42"
 *   ?selected=${sel}
 *   style="
 *     --filter-chip-selected-bg:     oklch(from var(--schmancy-sys-color-primary-default) l c h / 0.12);
 *     --filter-chip-selected-color:  var(--schmancy-sys-color-primary-default);
 *     --filter-chip-selected-border: oklch(from var(--schmancy-sys-color-primary-default) l c h / 0.40);
 *   "
 * >
 *   <span slot="leading" class="w-4 h-4 rounded-full bg-primary-default/20 flex items-center justify-center">
 *     <span class="text-[7px] font-bold">A</span>
 *   </span>
 *   Agent Name
 * </schmancy-filter-chip>
 * ```
 */
export class SchmancyFilterChip extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				outline: none;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg, line ~257 below) */
				transition: transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);

				/* The untoned defaults. The color/variant pair writes these same three
				   from the tone ladder; overriding them on the host is the escape hatch
				   for a look the ladder does not name. Shadow-DOM internals are not
				   part of the public API either way. */
				--filter-chip-selected-bg: var(--schmancy-sys-color-secondary-container);
				--filter-chip-selected-color: var(--schmancy-sys-color-secondary-onContainer);
				--filter-chip-selected-border: transparent;
			}

			/* A neutral ground: a scope is being read, not a choice being celebrated. */
			:host([density='compact']) {
				--filter-chip-selected-bg: var(--schmancy-sys-color-surface-containerHigh);
				--filter-chip-selected-color: var(--schmancy-sys-color-surface-on);
				border-radius: var(--schmancy-radius-xs, 0.4375rem);
			}
			:host([density='compact']) button {
				height: 2rem;
				padding-inline: 0.375rem;
				gap: 0.25rem;
				font-size: 0.75rem;
				line-height: 1rem;
			}
			@container (min-width: 36rem) {
				:host([density='compact']) button {
					height: 1.25rem;
					font-size: 0.6875rem;
				}
				/* Still 24px to a pointer, though it draws 20px. */
				:host([density='compact']) button::after {
					content: '';
					position: absolute;
					inset: -2px 0;
				}
			}

			:host([compact]) {
				border-radius: var(
					--schmancy-radius-full,
					calc(infinity * 1px)
				); /* mirrors rounded-full on the compact geometry branch below */
			}

			/* :not(:where([busy])) carries no specificity, so the reduced-motion rule
			   below still outranks this one. */
			:host(:active:not([disabled]):not([readonly]):not([density]):not(:where([busy]))) {
				transform: scale(0.95);
				transition-duration: 100ms;
			}

			:host([disabled]) {
				pointer-events: none;
				opacity: var(--schmancy-sys-state-opacity-disabled, 0.38);
			}

			/* pointer-events is inherited, so a disabled host would otherwise also
			   swallow clicks on trailing-slot content (e.g. select-role's read-only
			   "view permissions" affordance) — re-enable hit-testing there only.
			   Selection/deselection itself stays blocked: handleClick/handleKeyDown
			   both early-return on disabled/readonly regardless of pointer-events. */
			:host([disabled]) slot[name='trailing'] {
				pointer-events: auto;
			}

			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: none;
				}
				:host(:active:not([disabled]):not([readonly])) {
					transform: none;
				}
			}

			button {
				/* font-size lives on the Tailwind utility class (sizeClasses / compact's text-[11px]), never here. */
				font-family: inherit;
			}

			slot[name='leading'],
			slot[name='trailing'] {
				display: contents;
			}

			/* Busy: the label keeps its box and its accessible name, and hands its
			   ink to the spinner laid over it. Disabled wins, so it stays legible. */
			:host([busy]:not([disabled])) slot:not([name]) {
				color: transparent;
			}
		`,
	]

	@property({ type: String, reflect: true })
	value: string = ''

	#selected: boolean = false

	@property({ type: Boolean, reflect: true })
	get selected(): boolean {
		return this.#selected
	}
	set selected(value: boolean) {
		const oldValue = this.#selected
		this.#selected = value
		this.requestUpdate('selected', oldValue)
	}

	@property({ type: Boolean, reflect: true })
	removable: boolean = false

	/**
	 * Replaces the remove control's whole accessible name, which is otherwise
	 * `chip.remove` — `Remove {{name}}` in English — filled with the text this
	 * chip renders, so a row of chips answers to one name each rather than all to
	 * `Remove filter`. Still replaced whole rather than composed from a translated
	 * verb: the subject rides inside a sentence the wording table owns, because
	 * `${verb} ${noun}` concatenated here inverts in most of the languages this
	 * library ships to. `value` names the chip only when it renders no text at
	 * all, since a chip's value is as often an opaque id as it is the text on it.
	 */
	@property({ type: String, attribute: 'remove-label' })
	removeLabel: string = ''

	@property({ type: Boolean, reflect: true })
	disabled: boolean = false

	/**
	 * Shown, read, and not operable. The chip keeps its tab stop and keeps
	 * announcing which filter it is and whether that filter is on; what stops
	 * is every path that would dispatch — click, Enter/Space, and the remove
	 * control — so a caller displaying a set decided somewhere else needs no
	 * `inert`, which would take the whole thing out of the accessibility tree
	 * it is being displayed to be read from.
	 *
	 * What it announces is `aria-disabled`, not `aria-readonly`: this chip is a
	 * toggle BUTTON (see the render comment on why it cannot be a checkbox), and
	 * WAI-ARIA 1.2 lists `aria-readonly` under neither the button role's
	 * supported nor its inherited properties, while `aria-disabled` —
	 * "perceivable but not editable or otherwise operable" — is exactly the
	 * state and is supported there. `disabled` is the other word for it and
	 * drops the tab stop; `readonly` keeps it.
	 *
	 * The dim is deliberately NOT part of it: a caller displaying a region it
	 * does not own dims that region once, at the region.
	 */
	@property({ type: Boolean, reflect: true })
	readonly: boolean = false

	/**
	 * The save this chip's press started is in flight — usually set by its
	 * `<schmancy-chips>`, which forwards its own `busy` to the pressed chip. A
	 * spinner is laid over the chip in its own ink, with no dim and no change to
	 * its box; the label stays in place underneath, so the chip keeps its
	 * accessible name and its tab stop, and every press is ignored until it
	 * clears. `aria-busy` says "wait" where `aria-disabled` would say "you
	 * cannot". A chip that is also `disabled` is disabled.
	 */
	@property({ type: Boolean, reflect: true })
	busy: boolean = false

	@property({ type: Boolean, reflect: true })
	elevated: boolean = false

	/** Size from the shared control-size ladder. Default `'sm'` = 40px / 14px. Ignored when `compact` is set. */
	@property({ reflect: true })
	size: ControlSize = 'sm'

	/** Compact pill preset — wins over `size`; not a size-ladder step (`h-6 rounded-full pl-1 pr-2.5 gap-1.5 text-[11px]`, 24px/11px). */
	@property({ type: Boolean, reflect: true })
	compact: boolean = false

	/**
	 * `compact` makes the chip a segment in a tool's row, in place of `size` and `compact`: 20px at 11px
	 * where the nearest container is 36rem or wider, and 32px at 12px, a finger's, where it is narrower or
	 * there is none, with only the chosen one grounded. `schmancy-chips` sets it on its chips.
	 */
	@property({ reflect: true })
	density?: 'compact' | undefined

	/**
	 * Which hue the SELECTED pill carries, from the tone family. Unselected is
	 * the page's own `surface-container` at every tone — a scope switch reads as
	 * one row of pills where exactly one is lit, and a tone on the unlit ones
	 * would make the row look like eight competing states.
	 *
	 * Unset is `secondary`'s tinted pair. Set it, and an achromatic surface
	 * reaches its own selected look through the ladder instead of hand-wiring
	 * the three custom properties above: `color="surface" variant="solid"`
	 * inverts the pill — `surface.on` ground under `surface.default` ink — which
	 * is the maximum-contrast rung and carries no hue at all.
	 */
	@property({ reflect: true }) color?: SchmancyTone

	/**
	 * How hard the selected pill presses `color`, on the four-rung ladder every
	 * mark in the tone family sits on. Ignored while `color` is unset.
	 *
	 * `soft` is the default because it is what the untoned chip already paints.
	 * On `surface` it is the one rung that says nothing: `surface`'s `fill` IS
	 * `surface-container`, the unselected chip's own ground, so a soft surface
	 * pill paints selected and unselected identically — reach for `solid` there.
	 */
	@property({ reflect: true }) variant: ToneVariant = 'soft'

	constructor() {
		super()
		try {
			this.internals = this.attachInternals()
		} catch {
			this.internals = undefined
		}
	}

	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	static formAssociated = true
	internals: ElementInternals | undefined
	get form() {
		return this.internals?.form
	}

	connectedCallback() {
		super.connectedCallback()

		chipTextChanges(this)
			.pipe(
				tap(() => this.requestUpdate()),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	protected updated(changed: Map<string, unknown>) {
		super.updated?.(changed)
		if (changed.has('value') || changed.has('selected')) {
			this.internals?.setFormValue(this.#selected ? this.value || 'on' : null)
		}
	}

	formResetCallback(): void {
		this.#selected = this.hasAttribute('selected')
	}

	formDisabledCallback(disabled: boolean): void {
		this.disabled = disabled
	}

	private handleClick = () => {
		if (this.disabled || this.readonly || this.busy) return

		this.dispatchEvent(
			new CustomEvent('change', {
				detail: { value: this.value, selected: !this.#selected },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleRemove = (e: Event) => {
		if (this.disabled || this.readonly || this.busy) return

		e.stopPropagation()

		this.dispatchEvent(
			new CustomEvent('remove', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleKeyDown = (e: KeyboardEvent) => {
		if (this.disabled || this.readonly || this.busy) return

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			this.dispatchEvent(
				new CustomEvent('change', {
					detail: { value: this.value, selected: !this.#selected },
					bubbles: true,
					composed: true,
				}),
			)
		}
	}

	protected render(): unknown {
		// compact wins over size — fixed 24px pill; non-compact uses the size ladder.
		// The rung is a FLOOR, not an exact height: at every size the ladder's
		// single-line content box is <= the rung, so every one-line chip renders
		// the identical box it always has, and only a genuinely taller content
		// box — a stacked date pill, a two-line label — moves it instead of being
		// silently guillotined by a fixed height it never asked for.
		const dense = this.density === 'compact'
		const geometry = dense
			? { 'rounded-xs': true }
			: this.compact
			? { 'min-h-6 pl-1 pr-2.5 rounded-full gap-1.5 text-[11px]': true }
			: { [sizeClasses(this.size, 'chip', 'floor')]: true, 'rounded-lg': true }

		// The tone writes the same three custom properties the host exposes, so
		// there is one paint path and not two. `toneColors` leaves the hairline
		// undefined on every rung but `outline`; the untoned default is
		// `transparent`, and the control carries a border box at all times, so a
		// missing colour has to land there rather than on `currentColor`.
		const toned = this.color ? toneColors(this.color, this.variant) : undefined
		const tone = toned
			? `--filter-chip-selected-bg: ${toned.backgroundColor}; --filter-chip-selected-color: ${toned.color}; --filter-chip-selected-border: ${toned.borderColor ?? 'transparent'};`
			: ''

		// Inline style (not a class) so the CSS custom properties set on :host flow in.
		const selectedStyle = this.#selected
			? `${tone}background: var(--filter-chip-selected-bg); color: var(--filter-chip-selected-color); border-color: var(--filter-chip-selected-border);`
			: ''

		// Host classes are forwarded onto the inner button so a caller can restyle it.
		const hostClasses = Array.from(this.classList).reduce<Record<string, boolean>>((acc, cls) => {
			acc[cls] = true
			return acc
		}, {})

		const busy = this.busy && !this.disabled
		const operable = !this.disabled && !this.readonly && !busy

		const chipClasses = {
			'inline-flex': true,
			'items-center': true,
			'cursor-pointer': operable,
			'cursor-default': this.readonly && !this.disabled && !busy,
			'cursor-progress': busy,
			'transition-colors': true,
			'duration-200': true,
			'select-none': true,
			'font-medium': true,
			relative: true,
			border: true,
			'border-transparent': !this.#selected,

			...geometry,

			'bg-surface-container': !this.#selected && !dense,
			'text-surface-on': !this.#selected && !dense,
			'text-surface-onVariant': !this.#selected && dense,

			'hover:brightness-95': this.#selected && operable,
			'hover:bg-surface-containerHigh': !this.#selected && operable && !dense,
			'hover:bg-surface-container': !this.#selected && operable && dense,

			'active:brightness-90': operable,

			'focus-visible:outline-solid': !this.disabled,
			'focus-visible:outline-2': !this.disabled,
			'focus-visible:outline-offset-2': !this.disabled,
			'focus-visible:outline-primary-default': !this.disabled,

			'shadow-md': this.elevated && !this.disabled,
			'hover:shadow-lg': this.elevated && operable,

			'opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': this.disabled,
			'cursor-not-allowed': this.disabled,

			// Host classes trail last so they override component defaults
			...hostClasses,
		}

		// Toggle button (the native button role + aria-pressed), not checkbox:
		// aria-pressed is not allowed on role="checkbox", which reads aria-checked
		// instead, so the two together announced every chip as stateless. A toggle
		// button is also the only shape correct in both container modes —
		// schmancy-chips single-select would want radio semantics and multi
		// checkbox, and the chip cannot see which one it sits in.
		return html`
			<button
				${magnetic({ strength: dense || this.disabled || this.readonly || busy ? 0 : 2, radius: 40 })}
				class=${this.classMap(chipClasses)}
				style=${selectedStyle}
				@click=${this.handleClick}
				@keydown=${this.handleKeyDown}
				?disabled=${this.disabled}
				aria-label=${ifDefined(this.ariaLabel || undefined)}
				aria-pressed=${this.#selected ? 'true' : 'false'}
				aria-disabled=${this.disabled || this.readonly ? 'true' : 'false'}
				aria-busy=${ifDefined(busy ? 'true' : undefined)}
				tabindex="0"
			>
				<slot name="leading"></slot>
				<slot @slotchange=${() => this.requestUpdate()}></slot>
				<slot name="trailing"></slot>

				${
					this.removable
						? html`
								<button
									class="ml-1 -mr-1 size-6 inline-flex items-center justify-center rounded-full hover:bg-surface-containerHighest transition-colors duration-200"
									@click=${this.handleRemove}
									aria-label=${this.removeLabel || removeName(this)}
									tabindex="-1"
								>
									<schmancy-icon class="text-sm">close</schmancy-icon>
								</button>
							`
						: ''
				}
				${when(
					busy,
					() =>
						html`<span class="absolute inset-0 grid place-items-center" aria-hidden="true"
							><schmancy-spinner size=${this.compact ? 'xxs' : 'xs'}></schmancy-spinner
						></span>`,
				)}
			</button>
		`
	}
}

if (!customElements.get('schmancy-filter-chip')) {
	customElements.define('schmancy-filter-chip', SchmancyFilterChip)
}

if (!customElements.get('schmancy-chip')) {
	class SchmancyChipCompat extends SchmancyFilterChip {}
	customElements.define('schmancy-chip', SchmancyChipCompat)
}

export { SchmancyFilterChip as SchmancyChip }

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-chip': SchmancyFilterChip
		'schmancy-filter-chip': SchmancyFilterChip
	}
}

export type FilterChipChangeEvent = { value: string; selected: boolean }
export type FilterChipRemoveEvent = { value: string }
export type SchmancyChipChangeEvent = FilterChipChangeEvent
